import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActivatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Invalid activation link. Please check your email for the correct link.");
      setLoading(false);
      return;
    }

    // Verify the token
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${API}/invitations/verify/${token}`);
        setInvitation(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired activation link");
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/invitations/activate`, {
        token,
        password
      });
      setSuccess(true);
      toast.success("Account activated successfully!");
      // Redirect to login after 3 seconds
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to activate account");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 px-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto mb-4">
            <div className="w-20 h-20 rounded-full bg-[#F37021] flex items-center justify-center">
              <span className="text-3xl font-bold text-white">A</span>
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-[#333333]">
            {loading ? "Verifying..." : success ? "Welcome!" : error ? "Oops!" : "Set Your Password"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#F37021]" />
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-8">
              <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
              <p className="text-red-600 mb-6">{error}</p>
              <Button onClick={() => navigate("/login")} className="btn-primary">
                Go to Login
              </Button>
            </div>
          )}

          {success && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <p className="text-green-600 mb-2">Your account has been activated!</p>
              <p className="text-gray-600 mb-6">Redirecting to login...</p>
              <Button onClick={() => navigate("/login")} className="btn-primary">
                Go to Login Now
              </Button>
            </div>
          )}

          {invitation && !success && !error && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200 text-center">
                <p className="text-sm text-gray-600">Welcome,</p>
                <p className="font-bold text-[#333333] text-lg">{invitation.name}</p>
                <p className="text-sm text-gray-500">{invitation.email}</p>
              </div>

              <div>
                <Label htmlFor="password" className="input-label">Create Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                    className="pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm-password" className="input-label">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={submitting}
                data-testid="activate-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Activating...
                  </>
                ) : (
                  "Activate Account"
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
